import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { path_url } from "../../Config/config";
import { FaComments } from "react-icons/fa";

const interactionSchema = Yup.object().shape({
  direction: Yup.string().required("Direction is required"),
  product: Yup.string().required("Product is required"),
  status: Yup.string().required("Status is required"),
  preferences: Yup.string().required("Priority is required"),
  notes: Yup.string(),
});

const DirectionOptions = ["Outbound", "Inbound"];
const StatusOptions = ["Open", "Close"];
const PriorityOptions = [
  "High",
  "Medium",
  "Low",
  "Critical",
  "Normal",
  "Emergency",
  "Deferred",
];
const ProductOptions = ["CRM", "ERP", "HRM", "Accounting", "General"];

export default function CreateInteractionRecordModal({
  open,
  onClose,
  leadId,
  customerId,
  vendorId,
  onInteractionCreated,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        style: {
          borderRadius: "12px",
          padding: "8px",
        },
      }}
    >
      <DialogTitle className="d-flex justify-content-between align-items-center pb-2 border-bottom">
        <div
          className="d-flex align-items-center gap-2"
          style={{ color: "#17a2b8", fontWeight: 600 }}
        >
          <FaComments style={{ fontSize: "1.25rem" }} />
          <span>Create Interaction Record</span>
        </div>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="pt-4">
        <Formik
          initialValues={{
            direction: "Outbound",
            product: "CRM",
            status: "Close",
            preferences: "Medium",
            notes: "",
          }}
          validationSchema={interactionSchema}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              const token = localStorage.getItem("accessToken");
              const adminId = localStorage.getItem("adminId");
              const payload = {
                ...values,
                priority: values.priority || values.preferences || "Medium",
                enquiry: values.enquiry || values.notes || "",
                useradminid: adminId,
                usercid: adminId,
                ...(leadId ? { leadid: leadId } : {}),
                ...(customerId ? { customerid: customerId } : {}),
                ...(vendorId ? { vendorid: vendorId } : {}),
              };

              const response = await axios.post(
                `${path_url}/interactionrecord`,
                payload,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                }
              );

              if (
                response.data?.statusCodeValue === 200 ||
                response.status === 200 ||
                response.status === 201
              ) {
                toast.success("Interaction Record created successfully!");
                const createdRecord = response.data?.body || payload;
                if (onInteractionCreated) {
                  onInteractionCreated(createdRecord);
                }
                resetForm();
                onClose();
              } else {
                toast.error(response.data?.body || "Failed to create interaction record");
              }
            } catch (error) {
              console.error("Interaction record creation error:", error);
              toast.error(
                error.response?.data?.message ||
                  error.message ||
                  "Failed to create interaction record"
              );
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit} className="row g-3 mt-1">
              <div className="col-md-6 mb-3">
                <label className="form-label font-weight-bold">
                  Direction <span className="text-danger">*</span>
                </label>
                <select
                  name="direction"
                  className={`form-select ${
                    touched.direction && errors.direction ? "is-invalid" : ""
                  }`}
                  value={values.direction}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {DirectionOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {touched.direction && errors.direction && (
                  <div className="invalid-feedback">{errors.direction}</div>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label font-weight-bold">
                  Product <span className="text-danger">*</span>
                </label>
                <select
                  name="product"
                  className={`form-select ${
                    touched.product && errors.product ? "is-invalid" : ""
                  }`}
                  value={values.product}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {ProductOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {touched.product && errors.product && (
                  <div className="invalid-feedback">{errors.product}</div>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label font-weight-bold">
                  Status <span className="text-danger">*</span>
                </label>
                <select
                  name="status"
                  className={`form-select ${
                    touched.status && errors.status ? "is-invalid" : ""
                  }`}
                  value={values.status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {StatusOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {touched.status && errors.status && (
                  <div className="invalid-feedback">{errors.status}</div>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label font-weight-bold">
                  Priority / Preference <span className="text-danger">*</span>
                </label>
                <select
                  name="preferences"
                  className={`form-select ${
                    touched.preferences && errors.preferences
                      ? "is-invalid"
                      : ""
                  }`}
                  value={values.preferences}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {PriorityOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {touched.preferences && errors.preferences && (
                  <div className="invalid-feedback">{errors.preferences}</div>
                )}
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label font-weight-bold">Notes</label>
                <textarea
                  name="notes"
                  rows={4}
                  className="form-control"
                  placeholder="Interaction summary and notes..."
                  value={values.notes}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              <div className="col-12 d-flex justify-content-end gap-2 pt-3 border-top">
                <button
                  type="button"
                  className="btn btn-secondary px-4"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-info text-white px-4"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Create Interaction"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
